import { Controller } from "@hotwired/stimulus"
import Collapse from 'bootstrap/js/dist/collapse';

export default class extends Controller {
    static targets = ["collapseButton", "collapseContent"]

    connect() {
        this.jumpmarkFunc();
        this.initializeCollapse();
    }

    disconnect() {
        clearTimeout(this.copyLinkTitleTimeout);

        // Clean up Bootstrap collapse instance
        if (this.collapseInstance) {
            this.collapseInstance.dispose();
        }
    }

    initializeCollapse() {
        // Only initialize if targets exist
        if (!this.hasCollapseButtonTarget || !this.hasCollapseContentTarget) {
            return;
        }

        // Remove Bootstrap's automatic data-toggle handling to prevent conflicts
        this.collapseButtonTarget.removeAttribute('data-bs-toggle');
        
        // Create a single Bootstrap Collapse instance
        this.collapseInstance = new Collapse(this.collapseContentTarget, {
            toggle: false
        });

        // Handle button clicks manually
        this.collapseButtonTarget.addEventListener('click', this.handleToggle.bind(this));
    }

    handleToggle(event) {
        event.preventDefault();
        event.stopPropagation();

        if (!this.collapseInstance) return;

        const isExpanded = this.collapseButtonTarget.getAttribute('aria-expanded') === 'true';
        
        if (isExpanded) {
            this.collapseInstance.hide();
            this.collapseButtonTarget.setAttribute('aria-expanded', 'false');
            this.collapseButtonTarget.classList.add('collapsed');
        } else {
            this.collapseInstance.show();
            this.collapseButtonTarget.setAttribute('aria-expanded', 'true');
            this.collapseButtonTarget.classList.remove('collapsed');
        }
    }

    copyToClipboard(event) {
        event.preventDefault();

        const button = event.currentTarget;
        const url = button.dataset.url;

        if (!url) {
            return;
        }

        if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(url).catch(() => this.#copyUrlFallback(url));
        } else {
            this.#copyUrlFallback(url);
        }

        const successMessage = button.dataset.copySuccess;
        if (!successMessage) {
            return;
        }

        const originalTitle = button.getAttribute('title');
        button.setAttribute('title', successMessage);

        if (this.copyLinkTitleTimeout) {
            clearTimeout(this.copyLinkTitleTimeout);
        }

        this.copyLinkTitleTimeout = setTimeout(() => {
            button.setAttribute('title', originalTitle ?? '');
        }, 3000);
    }

    #copyUrlFallback(url) {
        const el = document.createElement('textarea');
        el.value = url;
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
    }

    jumpmarkFunc() {
        const headings = this.getHeadings();

        const jumpmarkTable = document.querySelector("#jumpmarks")

        headings.forEach((heading) => {
            const jumpmarkHeading = document.createElement('a');
            jumpmarkHeading.textContent = heading.text;
            jumpmarkHeading.href = `#${heading.id}`
            jumpmarkHeading.style.display = 'block';

            if (heading.level === 3 && heading.parent) {
                jumpmarkHeading.classList.add("sub-headline");
            }

            jumpmarkTable.appendChild(jumpmarkHeading);
        })
    }

    getHeadings() {
        const containers = document.querySelectorAll(".text1, .text2");
        if (!containers.length) return [];

        const slugify = (str) =>
            str.toLowerCase()
                .trim()
                .replace(/\s+/g, '-')
                .replace(/[^\w-]+/g, '')
                .replace(/--+/g, '-')
                .replace(/^-+|-+$/g, '');

        const usedIds = new Set();
        const results = [];

        containers.forEach(container => {

            let lastH2 = null;

            const headings = container.querySelectorAll('h1,h2,h3,h4,h5,h6');
            headings.forEach(h => {
                let slug = slugify(h.textContent);
                let uniqueId = slug;
                let counter = 1;

                while (usedIds.has(uniqueId)) {
                    uniqueId = `${slug}-${counter++}`;
                }

                usedIds.add(uniqueId);
                h.setAttribute('id', uniqueId);

                const level = parseInt(h.tagName.substring(1));

                let parent = null;
                if (level === 2) {
                    lastH2 = uniqueId;
                }
                if (level === 3 && lastH2) {
                    parent = lastH2;
                }

                results.push({
                    id: uniqueId,
                    text: h.textContent.trim(),
                    level,
                    parent
                });
            });
        });

        return results;
    }
}

